import ContentRoutes from 'ContentUtils/Routes';
import { getUsageTracker, trackFrameworkTableInteraction } from 'EmailComponents/utils/tracking';
import { AB_SELECT_VALUE } from 'EmailData/constants/details/abEmailConstants';
import EmailTemplateModes from 'EmailData/enums/EmailTemplateModes';
import { navigateTo } from 'EmailData/utils/url';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UISelect from 'UIComponents/input/UISelect';
import AILoadingButton from 'ai-components-ui-library/components/buttons/AILoadingButton';
import { AIBadge } from 'ai-components-ui-library/components/informative/AIBadge';
import useEmailDetails from 'email-management-lib/Shared/hooks/useEmailDetails';
import { generateTranslatedVersionRequest } from 'email-management-lib/refactor/api/emails';
import { HAS_USED_AI_TRANSLATION_KEY, setUserAttributeRequest } from 'email-management-lib/refactor/api/userAttributes';
import useHasUsedAITranslation from 'email-management-lib/refactor/hooks/useHasUsedAITranslation';
import useSupportedTranslationLanguages from 'email-management-lib/refactor/hooks/useSupportedTranslationLanguages';
import useTranslationInfo from 'email-management-lib/refactor/hooks/useTranslationInfo';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { BreakString } from 'foundations-components/transitional/components/BreakString';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { H5 } from 'foundations-components/transitional/components/H5';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Panel, PanelSection } from 'foundations-components/transitional/components/Panel';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { PanelFooter } from 'foundations-components/transitional/components/PanelFooter';
import { PanelHeader } from 'foundations-components/transitional/components/PanelHeader';
import { Small } from 'foundations-components/transitional/components/Small';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import Raven from 'raven-js';
import { useCallback, useMemo, useState } from 'react';
import AbOptionTranslation from './AbOptionTranslation';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export default function GenerateTranslatedVersionPanel({
  email,
  onClose
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [translatedEmailName, setTranslatedEmailName] = useState('');
  const [selectedVersion, setSelectedVersion] = useState(null);
  const {
    isConvertible
  } = useTranslationInfo({
    emailId: email.id
  });
  const {
    supportedTranslationLanguages,
    isFetchingSupportedTranslationLanguages,
    error
  } = useSupportedTranslationLanguages();
  const {
    hasUsedAiTranslation
  } = useHasUsedAITranslation();
  const {
    emailDetails,
    isFetchingEmailDetails
  } = useEmailDetails({
    emailId: email.id,
    skip: !email.isABLike()
  });
  const options = useMemo(() => {
    return supportedTranslationLanguages.reduce((acc, lang) => {
      if (lang.code !== email.language) {
        acc.push({
          value: lang.code,
          text: I18n.text(`email-management.modals.generateTranslatedVersion.languages.${lang.code}`)
        });
      }
      return acc;
    }, []);
  }, [email.language, supportedTranslationLanguages]);
  const handleSelectLanguage = useCallback(e => {
    const targetedLanguage = e.target.value;
    trackFrameworkTableInteraction({
      otherActions: 'select-translated-email-language'
    });
    setSelectedLanguage(targetedLanguage);
    setTranslatedEmailName(`${email.name} (${I18n.text(`email-management.modals.generateTranslatedVersion.languages.${targetedLanguage}`)})`);
  }, [email.name]);
  const handleChangeEmailName = useCallback(e => {
    trackFrameworkTableInteraction({
      otherActions: 'edit-translated-email-name'
    });
    setTranslatedEmailName(e.target.value);
  }, []);
  const isGenerateDisabled = useMemo(() => {
    return !selectedLanguage || !translatedEmailName;
  }, [selectedLanguage, translatedEmailName]);
  const handleGenerate = useCallback(() => {
    setIsLoading(true);
    if (!hasUsedAiTranslation) {
      setUserAttributeRequest(HAS_USED_AI_TRANSLATION_KEY, true).catch(e => Raven.captureException(e));
    }
    trackFrameworkTableInteraction({
      otherActions: 'confirm-translate-email'
    });
    trackFrameworkTableInteraction({
      otherActions: `targeted-translation-language-${selectedLanguage}`
    });
    generateTranslatedVersionRequest({
      emailId: email.isABLike() && selectedVersion === AB_SELECT_VALUE.B && emailDetails ? emailDetails.getLastTestVariantEmail().id : email.id,
      name: translatedEmailName,
      variantOnly: email.isABLike(),
      convertToDd: email.emailTemplateMode === EmailTemplateModes.DESIGN_MANAGER,
      subCategory: email.subcategory,
      folderId: email.folderId,
      language: selectedLanguage
    }).then(response => {
      onClose();
      navigateTo(ContentRoutes.editEmail(response.id));
    }).catch(e => {
      Raven.captureException(e);
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "email-management.modals.generateTranslatedVersion.errorResponse"
        }),
        type: 'danger',
        timeout: 10000
      });
      setIsLoading(false);
    });
  }, [email, emailDetails, hasUsedAiTranslation, onClose, selectedLanguage, selectedVersion, translatedEmailName]);
  const shouldRenderAbOptionTranslation = useMemo(() => {
    return email.isABLike() && emailDetails && !emailDetails.isEmpty() && !isFetchingEmailDetails;
  }, [email, emailDetails, isFetchingEmailDetails]);
  const onCancel = useCallback(() => {
    trackFrameworkTableInteraction({
      otherActions: 'cancel-translate-email'
    });
    onClose();
  }, [onClose]);
  return /*#__PURE__*/_jsxs(Panel, {
    width: 450,
    children: [/*#__PURE__*/_jsx(DialogCloseButton, {
      onClick: onClose
    }), /*#__PURE__*/_jsx(PanelHeader, {
      children: /*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "email-management.modals.generateTranslatedVersion.title"
        })
      })
    }), /*#__PURE__*/_jsx(PanelBody, {
      children: /*#__PURE__*/_jsxs(PanelSection, {
        children: [email.isABLike() ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "email-management.modals.generateTranslatedVersion.abDescription"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "email-management.modals.generateTranslatedVersion.description"
        }), /*#__PURE__*/_jsx(Text, {
          marginBlockStart: "400",
          typography: "detail-200",
          children: /*#__PURE__*/_jsx("strong", {
            children: email.isABLike() ? /*#__PURE__*/_jsx(FormattedMessage, {
              message: "email-management.modals.generateTranslatedVersion.abEmailName"
            }) : /*#__PURE__*/_jsx(FormattedMessage, {
              message: "email-management.modals.generateTranslatedVersion.emailName"
            })
          })
        }), /*#__PURE__*/_jsx(BreakString, {
          children: email.name
        }), /*#__PURE__*/_jsx(Text, {
          color: "do-not-use-heffalump",
          marginBlockStart: "400",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "email-management.modals.generateTranslatedVersion.emailLanguage"
          })
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: `email-management.modals.generateTranslatedVersion.languages.${email.language}`
        }), /*#__PURE__*/_jsxs(Flex, {
          justifyContent: "space-between",
          alignItems: "center",
          marginBlockEnd: "200",
          marginBlockStart: "800",
          children: [/*#__PURE__*/_jsxs(H5, {
            marginBlockEnd: "0",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "email-management.modals.generateTranslatedVersion.translate"
            }), /*#__PURE__*/_jsx(HelpIcon, {
              color: "icon-core-subtle",
              marginInlineStart: "100",
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "email-management.modals.generateTranslatedVersion.tooltip"
              }),
              tooltipPlacement: "right"
            })]
          }), /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            children: [/*#__PURE__*/_jsx(Small, {
              marginInlineEnd: "100",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "email-management.modals.generateTranslatedVersion.poweredBy"
              })
            }), /*#__PURE__*/_jsx(AIBadge, {})]
          })]
        }), isFetchingEmailDetails && /*#__PURE__*/_jsx(LoadingSpinner, {}), shouldRenderAbOptionTranslation && /*#__PURE__*/_jsx(AbOptionTranslation, {
          emailDetails: emailDetails,
          selectedVersion: selectedVersion,
          setSelectedVersion: setSelectedVersion
        }), /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "email-management.modals.generateTranslatedVersion.emailLanguage"
          }),
          children: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(UISelect, {
              "data-test-id": "language-select",
              options: options,
              isLoading: isFetchingSupportedTranslationLanguages,
              placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "email-management.modals.generateTranslatedVersion.placeHolder"
              }),
              onChange: handleSelectLanguage
            }), !!error && /*#__PURE__*/_jsx(Small, {
              use: "error",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "email-management.modals.generateTranslatedVersion.error"
              })
            })]
          })
        }), selectedLanguage && /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "email-management.modals.generateTranslatedVersion.emailName"
          }),
          children: /*#__PURE__*/_jsx(TextInput, {
            name: "translated-email-name",
            value: translatedEmailName,
            onChange: handleChangeEmailName
          })
        }), selectedLanguage && /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "email-management.modals.generateTranslatedVersion.settings"
          }),
          children: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "email-management.modals.generateTranslatedVersion.settingDescription"
            }), isConvertible === false && email.emailTemplateMode === EmailTemplateModes.DESIGN_MANAGER && /*#__PURE__*/_jsx(Alert, {
              marginBlockStart: "400",
              "data-test-id": "translation-alert",
              titleText: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "email-management.modals.generateTranslatedVersion.pleaseReview"
              }),
              children: /*#__PURE__*/_jsxs(List, {
                styled: true,
                paddingInlineStart: "500",
                children: [/*#__PURE__*/_jsx(ListItem, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "email-management.modals.generateTranslatedVersion.alertBody.list1"
                  })
                }), /*#__PURE__*/_jsx(ListItem, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "email-management.modals.generateTranslatedVersion.alertBody.list2"
                  })
                })]
              })
            })]
          })
        })]
      })
    }), /*#__PURE__*/_jsxs(PanelFooter, {
      children: [/*#__PURE__*/_jsx(AILoadingButton, {
        "data-test-id": "generate-confirm-button",
        loading: isLoading,
        onClick: handleGenerate,
        disabled: isGenerateDisabled,
        usageTracker: getUsageTracker(),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "email-management.modals.generateTranslatedVersion.generate"
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onCancel,
        use: "link",
        marginInlineStart: "600",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "email-management.modals.generateTranslatedVersion.cancel"
        })
      })]
    })]
  });
}